import { PencilAltIcon } from "@heroicons/react/outline";
import { IAttendantTemplateData, IAttendantTemplateForm, IAttendantTemplateItem, ISimpleEventDetail } from "interfaces";
import { cloneDeep, get } from "lodash"
import { useFieldArray, useFormContext } from "react-hook-form";
import { FieldForm } from "../fieldForm";
import { Fragment, useState } from "react";
import moment from "moment";
import { useTranslation } from "@refinedev/core";

interface IGroupFields extends IAttendantTemplateData {
  id: string;
}

interface IGroupFormProps {
  templateIndex: number;
  eventDetailData: ISimpleEventDetail;
  onSubmit: (data: IAttendantTemplateItem, callback?:() => void, callbackMessage?: string) => void;
}

const INITIAL_FIELD_MODAL_CONFIG = {
  open: false,
  index: -1,
}
export const GroupForm = ({templateIndex, onSubmit, eventDetailData}: IGroupFormProps) => {
  const { translate } = useTranslation();
  const [fieldModalConfig, setFieldModalConfig] = useState(INITIAL_FIELD_MODAL_CONFIG)

  const {
    control,
    formState: { errors },
    getValues,
    watch,
  } = useFormContext<IAttendantTemplateForm>(); // retrieve all hook methods

  const { fields } = useFieldArray({
    control,
    name: `templates.${templateIndex}.data`,
  });

  const groupsData = watch(`templates.${templateIndex}.data`);

  const isOpenRegistration = get(eventDetailData, "open_registration_date", "") 
    ? moment().utc().isAfter(moment(eventDetailData.open_registration_date).utc())
    : false;
  
  const _onCloseModal = () => {
    setFieldModalConfig(INITIAL_FIELD_MODAL_CONFIG);
  }

  return (
    <div className="px-4 py-4 sm:px-6">
      <div className="mb-4 sm:flex sm:items-center sm:justify-between">
        <p>{translate("eventSetup.participantTemplate.groups")}</p>
        <p>{translate("eventSetup.participantTemplate.templateId")}: {get(watch("templates")[templateIndex], "id", "")}</p>
      </div>
      <div className="grid sm:grid-cols-6 lg:gap-x-6">
        <div
          className="col-span-full"
        >
          {fields.map((field: IGroupFields, index: number) => (
            <div
              className={`border rounded-lg mb-4 ${
                get(errors, `templates.${templateIndex}.data.${index}`, "")
                  ? "border-red-500"
                  : "border-gray-300"
              }`}
              key={field.id}
            >
              <div className={`box-container p-0`}>
                <div
                  className="flex flex-wrap justify-between items-center px-4 py-4 sm:px-6 hover:cursor-pointer"
                  onClick={() => setFieldModalConfig({
                    index: index,
                    open: true,
                  })}
                >
                  <div className="flex flex-row flex-wrap items-center gap-x-3 shrink-0">
                    {eventDetailData.languages.map((lang, langIndex) => (
                      <Fragment key={lang}>
                        {langIndex !== 0 ? <span>•</span> : <></>}
                        <p className="text-sm" style={{wordBreak: "break-word"}}>
                          {get(watch("templates")[templateIndex].data[index].group, `label.${lang}`, "")}
                        </p>
                      </Fragment>
                    ))}
                  </div>
                  <div className="flex flex-row gap-x-2">
                    <p className="text-sm">{`${groupsData[index].fields.length} ${translate("eventSetup.participantTemplate.fields")}`}</p>
                    <PencilAltIcon className="w-5 h-5" />
                  </div>
                </div>
              </div>
            </div>
          ))}
          <FieldForm
            isOpenRegistration={isOpenRegistration}
            templateIndex={templateIndex}
            groupIndex={fieldModalConfig.index}
            groupFields={watch("templates")[templateIndex].data ?? []}
            languages={getValues("languages")}
            open={fieldModalConfig.open}
            onClose={_onCloseModal}
            onMoveField={(movedField, updatedCurrentGroupFields, currentGroupIndex, targetGroupIndex) => {
              const templateData: IAttendantTemplateItem = cloneDeep(getValues("templates")[templateIndex]);
              templateData.data[targetGroupIndex].fields.push(movedField); // add field to target group
              templateData.data[currentGroupIndex].fields = updatedCurrentGroupFields; // remove moved field in current group 

              onSubmit({
                ...templateData,
              }, _onCloseModal, translate("toastMessage.fieldMovedSuccessfully"))
            }}
            onSubmit={data => {
              const templateData: IAttendantTemplateItem = {...getValues("templates")[templateIndex]};
              onSubmit({
                ...templateData,
                data: templateData.data.map((group, groupIndex) => ({
                  ...group,
                  fields: groupIndex === fieldModalConfig.index ? data: group.fields 
                }))
              }, _onCloseModal)
            }}
          />
        </div>
      </div>
    </div>
  )
}